import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { SectionNav } from "@/components/section-nav";
import { NoticiaBody } from "@/components/noticia-body";

type PressLink = { title: string; url: string; image_url?: string };

type Noticia = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body_md: string | null;
  category: string | null;
  published_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  press_links: PressLink[] | null;
  media_links: PressLink[] | null;
};

type Related = {
  id: string;
  slug: string;
  title: string;
  og_image_url: string | null;
  published_at: string | null;
  category: string | null;
};

const fetchNoticia = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const supa = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: row, error } = await supa
      .from("noticias")
      .select("id,slug,title,excerpt,body_md,category,published_at,seo_title,seo_description,og_image_url,press_links,media_links")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw error;
    if (!row) throw notFound();

    const { data: rel } = await supa
      .from("noticias")
      .select("id,slug,title,og_image_url,published_at,category")
      .eq("is_published", true)
      .neq("id", row.id)
      .order("published_at", { ascending: false })
      .limit(6);

    return { noticia: row as unknown as Noticia, related: (rel ?? []) as Related[] };
  });

export const Route = createFileRoute("/comunicaciones/noticias/$slug")({
  loader: ({ params }) => fetchNoticia({ data: { slug: params.slug } }),
  head: ({ loaderData }) => {
    const n = loaderData?.noticia;
    const title = n?.seo_title ?? n?.title ?? "Noticia | Mine Class";
    const desc = n?.seo_description ?? n?.excerpt ?? "";
    const url = n ? `https://mine-class.com/comunicaciones/noticias/${n.slug}` : "";
    return {
      meta: [
        { title: `${title} | Mine Class` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        ...(url ? [{ property: "og:url", content: url }] : []),
        { property: "og:type", content: "article" },
        ...(n?.og_image_url ? [{ property: "og:image", content: n.og_image_url }] : []),
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: url ? [{ rel: "canonical", href: url }] : [],
      scripts: n
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                headline: n.seo_title ?? n.title,
                description: n.seo_description ?? n.excerpt ?? undefined,
                image: n.og_image_url ?? undefined,
                datePublished: n.published_at ?? undefined,
                articleSection: n.category ?? undefined,
                mainEntityOfPage: url,
                author: { "@type": "Organization", name: "Mine Class" },
                publisher: {
                  "@type": "Organization",
                  name: "Mine Class",
                  url: "https://mine-class.com/",
                },
              }),
            },
          ]
        : [],
    };
  },
  errorComponent: () => (
    <NoticiaShell>
      <p className="text-center py-16 text-[var(--color-on-surface-variant)]">No pudimos cargar esta noticia.</p>
    </NoticiaShell>
  ),
  notFoundComponent: () => (
    <NoticiaShell>
      <p className="text-center py-16 text-[var(--color-on-surface-variant)]">Noticia no encontrada.</p>
    </NoticiaShell>
  ),
  component: Page,
});

function NoticiaShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white min-h-screen flex flex-col">
      <SiteNav />
      <SectionNav section="comunicaciones" />
      <main className="flex-1 max-w-container-max mx-auto px-margin-desktop py-xl w-full">{children}</main>
      <SiteFooter />
    </div>
  );
}

function Page() {
  const { noticia: n, related } = Route.useLoaderData() as { noticia: Noticia; related: Related[] };
  const press: PressLink[] = n.press_links ?? [];
  const medios: PressLink[] = n.media_links ?? [];
  return (
    <NoticiaShell>
      <div className="mb-5">
        <Link to="/comunicaciones/noticias" className="text-sm underline text-[var(--color-brand-purple)]">
          ← Volver a Noticias
        </Link>
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-10">
        <article className="min-w-0">
          {n.category && (
            <span className="inline-block py-1 px-3 bg-[var(--color-brand-coral)] text-white font-mono-data text-xs tracking-widest uppercase">
              {n.category}
            </span>
          )}
          <h1 className="text-display-lg leading-tight mt-4 text-[var(--color-brand-purple)]">{n.title}</h1>
          {n.published_at && (
            <p className="text-sm text-[var(--color-on-surface-variant)] mt-2">
              {new Date(n.published_at).toLocaleDateString("es-CL", { dateStyle: "long" })}
            </p>
          )}
          {n.og_image_url && (
            <img
              src={n.og_image_url}
              alt={n.title}
              className="w-full h-auto max-h-[32rem] object-contain rounded-md mt-6 border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-low)]"
            />
          )}
          {n.excerpt && <p className="text-lg mt-6 text-[var(--color-on-surface-variant)]">{n.excerpt}</p>}
          {n.body_md && (
            <div className="mt-8">
              <NoticiaBody md={n.body_md} />
            </div>
          )}

          {press.length > 0 && (
            <section className="mt-12 border-t border-[var(--color-outline-variant)] pt-8">
              <h2 className="text-xs uppercase tracking-widest text-[var(--color-brand-coral)] font-bold mb-4">
                Mine Class en la prensa
              </h2>
              <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory">
                {press.map((p, i) => (
                  <a
                    key={i}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="snap-start shrink-0 w-64 border border-[var(--color-outline-variant)] hover:border-[var(--color-brand-coral)] transition-colors bg-white"
                  >
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.title} className="w-full h-36 object-cover" />
                    ) : (
                      <div className="w-full h-36 bg-[var(--color-surface-container-low)]" />
                    )}
                    <div className="p-3">
                      <p className="text-sm font-semibold text-[var(--color-brand-purple)] line-clamp-3">{p.title}</p>
                      <p className="text-[10px] text-[var(--color-on-surface-variant)] mt-2 truncate">
                        {(() => { try { return new URL(p.url).hostname.replace(/^www\./, ""); } catch { return p.url; } })()}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {medios.length > 0 && (
            <section className="mt-12 border-t border-[var(--color-outline-variant)] pt-8">
              <h2 className="text-xs uppercase tracking-widest text-[var(--color-brand-coral)] font-bold mb-4">
                Mine Class en Medios:
              </h2>
              <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory">
                {medios.map((p, i) => (
                  <a
                    key={i}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="snap-start shrink-0 w-64 border border-[var(--color-outline-variant)] hover:border-[var(--color-brand-coral)] transition-colors bg-white"
                  >
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.title} className="w-full h-36 object-cover" />
                    ) : (
                      <div className="w-full h-36 bg-[var(--color-surface-container-low)]" />
                    )}
                    <div className="p-3">
                      <p className="text-[10px] uppercase tracking-wider text-[var(--color-brand-coral)] font-bold mb-1 truncate">
                        {(() => { try { return new URL(p.url).hostname.replace(/^www\./, ""); } catch { return p.url; } })()}
                      </p>
                      <p className="text-sm font-semibold text-[var(--color-brand-purple)] line-clamp-3">{p.title}</p>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}
        </article>

        <aside className="lg:sticky lg:top-8 self-start">
          <h2 className="text-xs uppercase tracking-widest text-[var(--color-brand-coral)] font-bold mb-4">
            Lee también...
          </h2>
          {related.length === 0 ? (
            <p className="text-sm text-[var(--color-on-surface-variant)]">No hay más noticias.</p>
          ) : (
            <ul className="space-y-4">
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to="/comunicaciones/noticias/$slug"
                    params={{ slug: r.slug }}
                    className="flex gap-3 group"
                  >
                    <div className="w-20 h-20 shrink-0 bg-[var(--color-surface-container-low)] overflow-hidden">
                      {r.og_image_url && <img src={r.og_image_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0">
                      {r.category && (
                        <span className="text-[10px] uppercase tracking-wider text-[var(--color-brand-coral)] font-bold">
                          {r.category}
                        </span>
                      )}
                      <p className="text-sm font-semibold text-[var(--color-brand-purple)] group-hover:underline line-clamp-3">
                        {r.title}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </NoticiaShell>
  );
}
